import {
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
  Textarea,
  Modal,
  TextInput,
  useToast,
  Box,
} from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import { SettingsSmsEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  useCreateOneClinicBlockedPhoneNumberMutation,
  useFindManyClinicBlockedPhoneNumberQuery,
  useUpdateOneClinicBlockedPhoneNumberMutation,
} from 'shared/types/graphql';
import { cleanPhoneNumber } from 'shared/utils';

type BlockPhoneNumberModalProps = {
  isBlockNumberOpen: boolean;
  onBlockNumberClose: () => void;
  initialNumber?: string;
};

const BlockPhoneNumberModal = ({
  initialNumber,
  isBlockNumberOpen,
  onBlockNumberClose,
}: BlockPhoneNumberModalProps): JSX.Element => {
  const [blockPhoneNumberValidation, setBlockPhoneNumberValidation] = useState({ isValid: true, errorMessage: '' });
  const [notes, setNotes] = useState('');
  const history = useHistory();
  const { clinicUser, currentClinicId: clinicId } = useClinicUser();
  const { trackEvent } = useTracking();

  const toast = useToast();

  const [phoneNumber, setPhoneNumber] = useState(initialNumber || '');

  const {
    data: blockedNumbersData,
    loading: blockedNumbersLoading,
    refetch: refetchClinicBlockedNumbers,
  } = useFindManyClinicBlockedPhoneNumberQuery({
    variables: { where: { clinicId: { equals: clinicId } } },
  });

  useEffect(() => {
    setNotes(
      blockedNumbersData?.findManyClinicBlockedPhoneNumber?.find(
        (blockedNumber) => blockedNumber.phoneNumber === phoneNumber,
      )?.notes || '',
    );
  }, [blockedNumbersData, phoneNumber]);

  const [createBlockedNumber, { loading: isCreateBlockedPhoneNumberLoading }] =
    useCreateOneClinicBlockedPhoneNumberMutation();

  const [updateBlockedNumber] = useUpdateOneClinicBlockedPhoneNumberMutation();

  const handleBlockNumber = async (): Promise<void> => {
    try {
      const validation = blockingNumberValidation(phoneNumber);
      if (!validation.isValid) {
        setBlockPhoneNumberValidation(validation);
        return;
      }
      trackEvent(SettingsSmsEvents.SubmitBlockedNumber);
      if (
        blockedNumbersData?.findManyClinicBlockedPhoneNumber?.find(
          (blockedNumber) => blockedNumber.phoneNumber === phoneNumber,
        )
      ) {
        // update record
        const blockedNumberId =
          blockedNumbersData?.findManyClinicBlockedPhoneNumber?.find(
            (blockedNumber) => blockedNumber.phoneNumber === phoneNumber,
          )?.id || '';
        updateBlockedNumber({
          variables: {
            where: {
              id: blockedNumberId,
            },
            data: {
              unblockedBy: { disconnect: true },
              blockedBy: { connect: { id: clinicUser?.id || '' } },
              createdAt: new Date().toISOString(),
              notes,
            },
          },
        });
      } else {
        await createBlockedNumber({
          variables: {
            data: {
              clinic: { connect: { id: clinicId } },
              phoneNumber: phoneNumber,
              notes: notes,
              blockedBy: { connect: { id: clinicUser?.id || '' } },
            },
          },
        });
      }
      setBlockPhoneNumberValidation(validation);
      toast({
        title: `${phoneNumber} blocked successfully`,
        status: 'success',
        duration: 5000,
        buttonProps: {
          buttonText: 'View Blocked Phone Numbers',
          buttonPosition: 'bottomLeft',
          onClick: (): void => {
            history.push(`${RouteBasePaths.Settings}${SettingsRoutes.ClinicPhone}#blocked_phone_numbers`);
          },
        },
      });
      refetchClinicBlockedNumbers();
      onBlockNumberClose();
    } catch (e) {
      console.log(e);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        status: 'error',
      });
    }
  };

  const closeBlockNumberModal = (): void => {
    setBlockPhoneNumberValidation({ isValid: true, errorMessage: '' });
    setPhoneNumber(initialNumber || '');
    onBlockNumberClose();
  };

  const blockingNumberValidation = (phoneNumber: string): { isValid: boolean; errorMessage: string } => {
    const phoneNumberRegex = /^[1-9]\d{9}/;
    if (!phoneNumberRegex.test(phoneNumber) || phoneNumber.length !== 10) {
      return { isValid: false, errorMessage: 'Phone number is invalid' };
    }
    return { isValid: true, errorMessage: '' };
  };

  return (
    <Modal onClose={closeBlockNumberModal} isOpen={isBlockNumberOpen} size="md">
      <ModalHeader>Block Number</ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={5}>
        <TextInput
          label="Phone Number"
          errorText={blockPhoneNumberValidation.errorMessage}
          isInvalid={!blockPhoneNumberValidation.isValid}
          value={phoneNumber}
          onChange={(e): void => {
            const number = e.target.value;
            if (number.length > 10) {
              return;
            }
            setPhoneNumber(cleanPhoneNumber(number));
          }}
        />
        <Box mt="4" />
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e): void => {
            setNotes(e.target.value);
          }}
        />
      </ModalBody>
      <ModalFooterButtons
        onCancel={closeBlockNumberModal}
        onSubmit={handleBlockNumber}
        isLoading={isCreateBlockedPhoneNumberLoading || blockedNumbersLoading}
      />
    </Modal>
  );
};

export default BlockPhoneNumberModal;
